import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, Bell, Database, Shield, Save, AlertCircle, CheckCircle, Loader2, ChevronDown, ChevronUp, FileText, BarChart3, Megaphone, Trash2, Edit, Plus, Zap, Link } from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'
import { useAuth } from '../hooks/useAuth'

interface SystemSettings {
  maintenanceMode: boolean
  requireEmailVerification: boolean
  allowPublicSignup: boolean
  betaTestingEnabled: boolean
  paywallEnabled: boolean
}

interface Announcement {
  id: string
  title: string
  message: string
  linkUrl: string | null
  linkText: string | null
  displayMode: 'once' | 'every_login' | 'until_dismissed'
  reshowAfterHours: number | null
  startDate: string | null
  endDate: string | null
  isActive: boolean
  priority: number
  createdAt: string
  updatedAt: string
}

interface CollapsibleSectionProps {
  title: string
  icon: React.ReactNode
  iconBgColor: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CollapsibleSection({ title, icon, iconBgColor, isOpen, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconBgColor}`}>
            {icon}
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  )
}

export default function SettingsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isSuperAdmin = user?.role === 'super_admin'

  const [localSettings, setLocalSettings] = useState<SystemSettings>({
    maintenanceMode: false,
    requireEmailVerification: true,
    allowPublicSignup: true,
    betaTestingEnabled: false,
    paywallEnabled: false,
  })

  const [successMessage, setSuccessMessage] = useState('')

  const [isCertAnalysisOpen, setIsCertAnalysisOpen] = useState(false)
  const [isCeAnalysisOpen, setIsCeAnalysisOpen] = useState(false)
  const [isSystemReportingOpen, setIsSystemReportingOpen] = useState(false)
  const [isDatabaseBackupOpen, setIsDatabaseBackupOpen] = useState(false)
  const [isCredentialManagementOpen, setIsCredentialManagementOpen] = useState(false)
  const [isAnnouncementsOpen, setIsAnnouncementsOpen] = useState(false)

  const [certModel, setCertModel] = useState('gemini-2.0-flash')
  const [certContent, setCertContent] = useState('')
  const [ceModel, setCeModel] = useState('gemini-2.0-flash')
  const [ceContent, setCeContent] = useState('')
  const [spreadsheetId, setSpreadsheetId] = useState('')
  const [userDataTab, setUserDataTab] = useState('')
  const [certDataTab, setCertDataTab] = useState('')
  const [lastUserSyncRun, setLastUserSyncRun] = useState('')
  const [lastUserSyncStatus, setLastUserSyncStatus] = useState('')
  const [lastUserSyncCount, setLastUserSyncCount] = useState('')
  const [lastCertSyncRun, setLastCertSyncRun] = useState('')
  const [lastCertSyncStatus, setLastCertSyncStatus] = useState('')
  const [lastCertSyncCount, setLastCertSyncCount] = useState('')
  const [lastBackupRun, setLastBackupRun] = useState('')
  const [lastBackupStatus, setLastBackupStatus] = useState('')
  const [lastBackupAttempt, setLastBackupAttempt] = useState('')

  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null)
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    linkUrl: '',
    linkText: '',
    displayMode: 'once' as 'once' | 'every_login' | 'until_dismissed',
    reshowAfterHours: '' as string | number | null,
    startDate: '',
    endDate: '',
    isActive: true,
    priority: 0,
  })

  const { data: settingsData, isLoading: isLoadingSettings } = useQuery<{ settings: Record<string, string> }>({
    queryKey: ['/api/admin/settings'],
    queryFn: () => apiGet<{ settings: Record<string, string> }>('/admin/settings'),
    enabled: isSuperAdmin,
  })

  const { data: announcementsData, isLoading: isLoadingAnnouncements } = useQuery<{ announcements: Announcement[] }>({
    queryKey: ['/api/admin/announcements'],
    queryFn: () => apiGet<{ announcements: Announcement[] }>('/admin/announcements'),
    enabled: isSuperAdmin,
  })

  useEffect(() => {
    if (settingsData?.settings) {
      const s = settingsData.settings
      setLocalSettings({
        maintenanceMode: s.maintenanceMode === 'true',
        requireEmailVerification: s.requireEmailVerification !== 'false',
        allowPublicSignup: s.allowPublicSignup !== 'false',
        betaTestingEnabled: s.betaTestingEnabled === 'true',
        paywallEnabled: s.paywallEnabled === 'true',
      })
      setCertModel(s.certAnalysisModel || 'gemini-2.0-flash')
      setCertContent(s.certAnalysisContent || '')
      setCeModel(s.ceAnalysisModel || 'gemini-2.0-flash')
      setCeContent(s.ceAnalysisContent || '')
      setSpreadsheetId(s.spreadsheetId || '')
      setUserDataTab(s.userDataTab || '')
      setCertDataTab(s.certDataTab || '')
      setLastUserSyncRun(s.lastSheetsSyncRun || '')
      setLastUserSyncStatus(s.lastSheetsSyncStatus || '')
      setLastUserSyncCount(s.lastSheetsSyncCount || '')
      setLastCertSyncRun(s.lastCertSyncRun || '')
      setLastCertSyncStatus(s.lastCertSyncStatus || '')
      setLastCertSyncCount(s.lastCertSyncCount || '')
      setLastBackupRun(s.lastDatabaseBackupRun || '')
      setLastBackupStatus(s.lastDatabaseBackupStatus || '')
      setLastBackupAttempt(s.lastDatabaseBackupAttempt || '')
    }
  }, [settingsData])

  const saveSettingsMutation = useMutation({
    mutationFn: (settings: Record<string, string>) => 
      apiPut('/admin/settings', { settings }),
    onSuccess: () => {
      setSuccessMessage('Settings saved successfully')
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings'] })
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const handleSettingChange = (key: keyof SystemSettings, value: boolean) => {
    const newSettings = { ...localSettings, [key]: value }
    setLocalSettings(newSettings)
    saveSettingsMutation.mutate({
      maintenanceMode: String(newSettings.maintenanceMode),
      requireEmailVerification: String(newSettings.requireEmailVerification),
      allowPublicSignup: String(newSettings.allowPublicSignup),
      betaTestingEnabled: String(newSettings.betaTestingEnabled),
      paywallEnabled: String(newSettings.paywallEnabled),
    })
  }

  const handleSaveAnalysisSettings = () => {
    saveSettingsMutation.mutate({
      certAnalysisModel: certModel,
      certAnalysisContent: certContent,
      ceAnalysisModel: ceModel,
      ceAnalysisContent: ceContent,
    })
  }

  const handleSaveReportingSettings = () => {
    saveSettingsMutation.mutate({
      spreadsheetId,
      userDataTab,
      certDataTab,
    })
  }

  const syncMutation = useMutation({
    mutationFn: () => apiPost('/admin/sync-users-to-sheets'),
    onSuccess: () => {
      setSuccessMessage('Users and certifications synced to Google Sheets')
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings'] })
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const backupMutation = useMutation({
    mutationFn: () => apiPost('/admin/backup-database'),
    onSuccess: (data: any) => {
      setSuccessMessage(data?.message || 'Database backup initiated successfully')
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings'] })
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const seedCeMutation = useMutation({
    mutationFn: () => apiPost('/admin/ce/seed'),
    onSuccess: (data: any) => {
      setSuccessMessage(`CE data seeded: ${data.professionsCreated} professions, ${data.requirementsCreated} requirements, ${data.categoriesCreated} categories`)
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const autoLinkMutation = useMutation({
    mutationFn: () => apiPost('/admin/credentials/auto-link-all'),
    onSuccess: (data: any) => {
      setSuccessMessage(data?.message || 'Auto-linked certifications to credential types')
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const createAnnouncementMutation = useMutation({
    mutationFn: (data: typeof announcementForm) => apiPost('/admin/announcements', data),
    onSuccess: () => {
      setSuccessMessage('Announcement created')
      queryClient.invalidateQueries({ queryKey: ['/api/admin/announcements'] })
      resetAnnouncementForm()
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const updateAnnouncementMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof announcementForm }) => 
      apiPut(`/admin/announcements/${id}`, data),
    onSuccess: () => {
      setSuccessMessage('Announcement updated')
      queryClient.invalidateQueries({ queryKey: ['/api/admin/announcements'] })
      resetAnnouncementForm()
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const deleteAnnouncementMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/announcements/${id}`),
    onSuccess: () => {
      setSuccessMessage('Announcement deleted')
      queryClient.invalidateQueries({ queryKey: ['/api/admin/announcements'] })
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const resetAnnouncementForm = () => {
    setAnnouncementForm({
      title: '',
      message: '',
      linkUrl: '',
      linkText: '',
      displayMode: 'once',
      reshowAfterHours: '',
      startDate: '',
      endDate: '',
      isActive: true,
      priority: 0,
    })
    setEditingAnnouncement(null)
    setShowAnnouncementForm(false)
  }

  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement)
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      linkUrl: announcement.linkUrl || '',
      linkText: announcement.linkText || '',
      displayMode: announcement.displayMode,
      reshowAfterHours: announcement.reshowAfterHours || '',
      startDate: announcement.startDate ? announcement.startDate.split('T')[0] : '',
      endDate: announcement.endDate ? announcement.endDate.split('T')[0] : '',
      isActive: announcement.isActive,
      priority: announcement.priority,
    })
    setShowAnnouncementForm(true)
  }

  const handleSaveAnnouncement = () => {
    if (!announcementForm.title || !announcementForm.message) {
      alert('Title and message are required')
      return
    }
    const payload = {
      ...announcementForm,
      reshowAfterHours: announcementForm.reshowAfterHours === '' ? null : Number(announcementForm.reshowAfterHours) || null,
    }
    if (editingAnnouncement) {
      updateAnnouncementMutation.mutate({ id: editingAnnouncement.id, data: payload })
    } else {
      createAnnouncementMutation.mutate(payload)
    }
  }

  const handleDeleteAnnouncement = (id: string) => {
    if (confirm('Are you sure you want to delete this announcement?')) {
      deleteAnnouncementMutation.mutate(id)
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Never'
    try {
      return new Date(dateStr).toLocaleString()
    } catch {
      return dateStr
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500">Only super admins can access system settings.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-500 mt-1">Configure global application behavior</p>
      </div>

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Settings className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">General</h2>
        </div>

        {isLoadingSettings ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Maintenance Mode</p>
                <p className="text-sm text-gray-500">Disable access for non-admin users</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localSettings.maintenanceMode}
                  onChange={() => handleSettingChange('maintenanceMode', !localSettings.maintenanceMode)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Require Email Verification</p>
                <p className="text-sm text-gray-500">Users must verify email before access</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localSettings.requireEmailVerification}
                  onChange={() => handleSettingChange('requireEmailVerification', !localSettings.requireEmailVerification)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Allow Public Signup</p>
                <p className="text-sm text-gray-500">Allow new users to register</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localSettings.allowPublicSignup}
                  onChange={() => handleSettingChange('allowPublicSignup', !localSettings.allowPublicSignup)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Paywall</p>
                <p className="text-sm text-gray-500">Require subscription for full access</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localSettings.paywallEnabled}
                  onChange={() => handleSettingChange('paywallEnabled', !localSettings.paywallEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-900">Beta Testing Mode</p>
                <p className="text-sm text-gray-500">Enable beta features and bug reporting</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localSettings.betaTestingEnabled}
                  onChange={() => handleSettingChange('betaTestingEnabled', !localSettings.betaTestingEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        )}
      </div>

      <CollapsibleSection
        title="Certification Analysis"
        icon={<FileText className="w-5 h-5 text-purple-600" />}
        iconBgColor="bg-purple-50"
        isOpen={isCertAnalysisOpen}
        onToggle={() => setIsCertAnalysisOpen(!isCertAnalysisOpen)}
      >
        <div className="pt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <input
              type="text"
              value={certModel}
              onChange={(e) => setCertModel(e.target.value)}
              placeholder="e.g. gemini-2.0-flash"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
            <textarea
              value={certContent}
              onChange={(e) => setCertContent(e.target.value)}
              placeholder="Enter system instructions for certification AI analysis..."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSaveAnalysisSettings}
            disabled={saveSettingsMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saveSettingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Analysis Settings
          </button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="CE Certificate Analysis"
        icon={<Zap className="w-5 h-5 text-yellow-600" />}
        iconBgColor="bg-yellow-50"
        isOpen={isCeAnalysisOpen}
        onToggle={() => setIsCeAnalysisOpen(!isCeAnalysisOpen)}
      >
        <div className="pt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <input
              type="text"
              value={ceModel}
              onChange={(e) => setCeModel(e.target.value)}
              placeholder="e.g. gemini-2.0-flash"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
            <textarea
              value={ceContent}
              onChange={(e) => setCeContent(e.target.value)}
              placeholder="Enter system instructions for CE certificate analysis..."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSaveAnalysisSettings}
            disabled={saveSettingsMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saveSettingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Analysis Settings
          </button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="System Reporting"
        icon={<BarChart3 className="w-5 h-5 text-green-600" />}
        iconBgColor="bg-green-50"
        isOpen={isSystemReportingOpen}
        onToggle={() => setIsSystemReportingOpen(!isSystemReportingOpen)}
      >
        <div className="pt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Google Spreadsheet ID</label>
            <input
              type="text"
              value={spreadsheetId}
              onChange={(e) => setSpreadsheetId(e.target.value)}
              placeholder="Enter Google Spreadsheet ID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Data Tab</label>
              <input
                type="text"
                value={userDataTab}
                onChange={(e) => setUserDataTab(e.target.value)}
                placeholder="e.g. Users"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cert Data Tab</label>
              <input
                type="text"
                value={certDataTab}
                onChange={(e) => setCertDataTab(e.target.value)}
                placeholder="e.g. Certifications"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <p className="text-sm text-gray-500">Users sync daily at 3am CST, certifications at 4am CST</p>

          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Last User Sync</p>
              <p className="text-sm text-gray-500">{formatDate(lastUserSyncRun)}</p>
              {lastUserSyncStatus && (
                <p className={`text-sm ${lastUserSyncStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {lastUserSyncStatus} {lastUserSyncCount && `(${lastUserSyncCount} rows)`}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Last Cert Sync</p>
              <p className="text-sm text-gray-500">{formatDate(lastCertSyncRun)}</p>
              {lastCertSyncStatus && (
                <p className={`text-sm ${lastCertSyncStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {lastCertSyncStatus} {lastCertSyncCount && `(${lastCertSyncCount} rows)`}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSaveReportingSettings}
              disabled={saveSettingsMutation.isPending}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saveSettingsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </button>
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending || !spreadsheetId}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {syncMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Sync Now
            </button>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Database Backup"
        icon={<Database className="w-5 h-5 text-amber-600" />}
        iconBgColor="bg-amber-50"
        isOpen={isDatabaseBackupOpen}
        onToggle={() => setIsDatabaseBackupOpen(!isDatabaseBackupOpen)}
      >
        <div className="pt-4 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Last Backup</p>
                <p className="text-sm text-gray-500">{formatDate(lastBackupRun)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Status</p>
                <p className={`text-sm ${lastBackupStatus === 'success' ? 'text-green-600' : lastBackupStatus === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>
                  {lastBackupStatus || 'Never run'}
                </p>
              </div>
            </div>
            {lastBackupAttempt && (
              <p className="text-xs text-gray-400 mt-2">Last attempt: {formatDate(lastBackupAttempt)}</p>
            )}
          </div>

          <p className="text-sm text-gray-500">Backups run automatically daily at 4am CST</p>

          <div className="flex gap-2">
            <button
              onClick={() => backupMutation.mutate()}
              disabled={backupMutation.isPending}
              className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {backupMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              Backup Now
            </button>
            <button
              onClick={() => seedCeMutation.mutate()}
              disabled={seedCeMutation.isPending}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              {seedCeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Seed CE Data
            </button>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Credential Management"
        icon={<Link className="w-5 h-5 text-purple-600" />}
        iconBgColor="bg-purple-50"
        isOpen={isCredentialManagementOpen}
        onToggle={() => setIsCredentialManagementOpen(!isCredentialManagementOpen)}
      >
        <div className="pt-4 space-y-4">
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <h3 className="font-medium text-purple-900 mb-2">Auto-Link Credentials</h3>
            <p className="text-sm text-purple-700 mb-4">
              Links all unlinked certifications to their matching approved credential types. 
              This enables automatic CE credit tracking for certifications that were uploaded 
              before the CE feature was implemented.
            </p>
            <button
              onClick={() => autoLinkMutation.mutate()}
              disabled={autoLinkMutation.isPending}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {autoLinkMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link className="w-4 h-4 mr-2" />}
              Auto-Link All Credentials
            </button>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Announcements"
        icon={<Megaphone className="w-5 h-5 text-pink-600" />}
        iconBgColor="bg-pink-50"
        isOpen={isAnnouncementsOpen}
        onToggle={() => setIsAnnouncementsOpen(!isAnnouncementsOpen)}
      >
        <div className="pt-4 space-y-4">
          {!showAnnouncementForm ? (
            <>
              <button
                onClick={() => setShowAnnouncementForm(true)}
                className="inline-flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </button>

              {isLoadingAnnouncements ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : announcementsData?.announcements && announcementsData.announcements.length > 0 ? (
                <div className="space-y-3">
                  {announcementsData.announcements.map((ann) => (
                    <div key={ann.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{ann.title}</h4>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${ann.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {ann.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                              {ann.displayMode}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ann.message}</p>
                          {ann.linkUrl && (
                            <p className="text-xs text-blue-500 mt-1">{ann.linkText || ann.linkUrl}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            Priority: {ann.priority}{ann.reshowAfterHours ? ` | Reshow: every ${ann.reshowAfterHours}h` : ''} | Created: {formatDate(ann.createdAt)}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditAnnouncement(ann)}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAnnouncement(ann.id)}
                            disabled={deleteAnnouncementMutation.isPending}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No announcements yet</p>
              )}
            </>
          ) : (
            <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-900">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={announcementForm.message}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
                  <input
                    type="text"
                    value={announcementForm.linkUrl}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, linkUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link Text</label>
                  <input
                    type="text"
                    value={announcementForm.linkText}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, linkText: e.target.value })}
                    placeholder="Click here"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Mode</label>
                  <select
                    value={announcementForm.displayMode}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, displayMode: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="once">Once</option>
                    <option value="every_login">Every Login</option>
                    <option value="until_dismissed">Until Dismissed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reshow After (hours)</label>
                  <input
                    type="number"
                    min="0"
                    value={announcementForm.reshowAfterHours ?? ''}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, reshowAfterHours: e.target.value === '' ? '' : parseInt(e.target.value) || 0 })}
                    placeholder="Never"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={announcementForm.priority}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={announcementForm.isActive}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, isActive: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={announcementForm.startDate}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={announcementForm.endDate}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSaveAnnouncement}
                  disabled={createAnnouncementMutation.isPending || updateAnnouncementMutation.isPending}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {(createAnnouncementMutation.isPending || updateAnnouncementMutation.isPending) ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editingAnnouncement ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={resetAnnouncementForm}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {saveSettingsMutation.isPending && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving settings...
        </div>
      )}
    </div>
  )
}
